import React from "react";

import { useLocalization } from "@fluent/react";
import { Button, Drawer, LoadingSkeleton } from "@narmi/design_system";

import { useReceipt } from "../../hooks/useReceipt";
import ReceiptDetailsCard from "../ReceiptDetailsDrawer/ReceiptDetailsCard";
import ReceiptImagePreview from "../ReceiptDetailsDrawer/ReceiptImagePreview";

import MatchedTransactionCard from "./MatchedTransactionCard";
import styles from "./MatchedReceiptDrawer.module.scss";

import type { SuggestedMatch } from "../../hooks/useReceiptsList";

// Separate from ReceiptDetailsDrawer: matched flow has no accept/reject footer
// and pulls matched-txn data from the list row — folding it into the Pending
// drawer would require several mutually-exclusive props.
interface MatchedReceiptDrawerProps {
  /** UUID of the matched receipt to display, or null to keep the drawer unmounted. */
  uuid: string | null;
  /** UUIDs of the matched rows in display order — drives prev/next navigation. */
  uuids: string[];
  /** Select another receipt (prev/next navigation). */
  onSelect: (uuid: string) => void;
  /** Matched transaction summary from the list row — drives the matched-transaction card. */
  matchedTransaction: SuggestedMatch | null;
  /** Suggestion id used to unlink the match; null disables the Unlink action. */
  suggestionId?: number | null;
  /** Invoked when the user clicks Unlink. Optimistic state lives in the caller's hook. */
  onUnlinkMatch?: (params: { suggestionId: number; receiptId: string }) => void;
  /** Disables the Unlink button while a mutation is in flight. */
  isUnlinkPending?: boolean;
  onClose: () => void;
}

interface OpenDrawerProps {
  uuid: string;
  uuids: string[];
  onSelect: (uuid: string) => void;
  matchedTransaction: SuggestedMatch | null;
  suggestionId?: number | null;
  onUnlinkMatch?: (params: { suggestionId: number; receiptId: string }) => void;
  isUnlinkPending?: boolean;
  onClose: () => void;
}

const OpenDrawer = ({
  uuid,
  uuids,
  onSelect,
  matchedTransaction,
  suggestionId,
  onUnlinkMatch,
  isUnlinkPending = false,
  onClose,
}: OpenDrawerProps) => {
  const { l10n } = useLocalization();
  const { data, isLoading, isError, refetch } = useReceipt(uuid);

  const currentIndex = uuids.indexOf(uuid);
  const prevUuid = currentIndex > 0 ? uuids[currentIndex - 1] : null;
  const nextUuid =
    currentIndex >= 0 && currentIndex < uuids.length - 1
      ? uuids[currentIndex + 1]
      : null;
  const handlePrev = prevUuid ? () => onSelect(prevUuid) : undefined;
  const handleNext = nextUuid ? () => onSelect(nextUuid) : undefined;

  const canShowUnlink = !!onUnlinkMatch;
  const canUnlink =
    canShowUnlink && typeof suggestionId === "number" && !isUnlinkPending;

  const handleUnlinkClick = () => {
    if (typeof suggestionId !== "number" || !onUnlinkMatch) return;
    onUnlinkMatch({ suggestionId, receiptId: uuid });
  };

  const footer = canShowUnlink ? (
    <div className={styles["footer-actions"]}>
      <Button
        kind="secondary"
        label={l10n.getString("receipts-drawer-unlink")}
        onClick={handleUnlinkClick}
        disabled={!canUnlink}
        testId="matched-receipt-drawer-unlink"
      />
    </div>
  ) : undefined;

  const renderBody = () => {
    if (isLoading) {
      return (
        <div data-testid="matched-receipt-drawer-loading">
          <LoadingSkeleton isLoading content="headerText" size="large" />
          <LoadingSkeleton isLoading content="paragraph" lines={4} />
        </div>
      );
    }

    if (isError || !data) {
      return (
        <div
          className={styles.error}
          data-testid="matched-receipt-drawer-error"
        >
          <p className="fontColor--primary">
            {l10n.getString("receipts-drawer-error-load")}
          </p>
          <Button
            kind="secondary"
            label={l10n.getString("receipts-drawer-error-retry")}
            onClick={() => refetch()}
            testId="matched-receipt-drawer-retry"
          />
        </div>
      );
    }

    return (
      <div className={styles["drawer-body"]}>
        <h2 className={`${styles.merchant} fontFamily--heading`}>
          {data.merchant ?? l10n.getString("receipts-table-unknown-merchant")}
        </h2>
        <ReceiptImagePreview
          imageUrl={data.image_url}
          mimeType={data.image_mime_type}
          thumbnailUrl={data.thumbnail_url}
          largeThumbnailUrl={data.large_thumbnail_url}
        />
        <ReceiptDetailsCard
          merchant={data.merchant}
          transactionDate={data.transaction_date}
          amount={data.amount}
        />
        <div>
          <p className="nds-typescale--upper fontColor--secondary fontWeight--bold fontSize--xs">
            {l10n.getString("receipts-drawer-matched-transaction")}
          </p>
          <div className="margin--top--xs">
            {matchedTransaction ? (
              <MatchedTransactionCard match={matchedTransaction} />
            ) : (
              <p
                className="fontColor--secondary"
                data-testid="matched-transaction-empty"
              >
                {l10n.getString("receipts-drawer-matched-transaction-empty")}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      isOpen
      onUserDismiss={onClose}
      onPrev={handlePrev}
      onNext={handleNext}
      position="right"
      depth="580px"
      paddingSize="xl"
      footer={footer}
      testId="matched-receipt-drawer"
    >
      {renderBody()}
    </Drawer>
  );
};

const MatchedReceiptDrawer = ({
  uuid,
  uuids,
  onSelect,
  matchedTransaction,
  suggestionId,
  onUnlinkMatch,
  isUnlinkPending,
  onClose,
}: MatchedReceiptDrawerProps) => {
  // Mount-gated so query state tears down on close — keeps presigned S3 URLs
  // (5-minute TTL) out of long-lived React Query cache entries.
  if (uuid === null) {
    return null;
  }

  return (
    <OpenDrawer
      uuid={uuid}
      uuids={uuids}
      onSelect={onSelect}
      matchedTransaction={matchedTransaction}
      suggestionId={suggestionId}
      onUnlinkMatch={onUnlinkMatch}
      isUnlinkPending={isUnlinkPending}
      onClose={onClose}
    />
  );
};

export default MatchedReceiptDrawer;
